import { Multiselect } from "@dashboard/components/Combobox";
import FormSpacer from "@dashboard/components/FormSpacer";
import useProductSearch from "@dashboard/searches/useProductSearch";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { Option } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";

import { sectionMessages } from "../../messages";
import { WsmProductRef } from "../../types";

interface WsmProductsFieldProps {
  value: WsmProductRef[];
  disabled?: boolean;
  error?: string;
  onChange: (products: WsmProductRef[]) => void;
  /** Another section's wording; the compose wording is the default. */
  label?: string;
  helperText?: string;
  "data-test-id"?: string;
}

/**
 * Every product that asks this question. A question is SHARED, so what is already
 * picked is seeded as an option in its own right: the field is fed by a search,
 * and a search for "cover" must not drop the product attached last week.
 */
export const WsmProductsField = ({
  value,
  disabled,
  error,
  onChange,
  label,
  helperText,
  "data-test-id": testId = "wsm-compose-products-field",
}: WsmProductsFieldProps) => {
  const intl = useIntl();
  const { search, result } = useProductSearch({
    variables: { after: null, first: 20, query: "" },
  });
  const selected: Option[] = value.map(product => ({
    label: product.name,
    value: product.id,
  }));
  const options: Option[] = [
    ...selected,
    ...(mapEdgesToItems(result.data?.search) ?? [])
      .filter(product => !value.some(picked => picked.id === product.id))
      .map(product => ({ label: product.name, value: product.id })),
  ];

  return (
    <>
      <Multiselect
        name="products"
        label={label ?? intl.formatMessage(sectionMessages.products)}
        data-test-id={testId}
        disabled={disabled}
        options={options}
        value={selected}
        loading={result.loading}
        fetchOptions={search}
        onChange={event =>
          onChange(
            ((event.target.value ?? []) as Option[]).map(option => ({
              id: option.value,
              name: option.label,
            })),
          )
        }
        error={!!error}
        helperText={error ?? helperText ?? intl.formatMessage(sectionMessages.productsHelp)}
        alwaysFetchOnFocus
      />
      <FormSpacer />
    </>
  );
};

export default WsmProductsField;
